import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { appLogger } from '@/utils/app-logger/appLogger';
import { isAppDebug } from '@/utils/appDebug';
import {
    captureDebugError,
    clearDebugError,
    type DebugErrorDetail,
    registerDebugErrorWindowCapture,
} from '@/utils/devtools/debug-error-dev-tool';

function getPathnameFromRouteUrl(url: string): string {
    return url.split('?')[0]?.split('#')[0] ?? url;
}

function getCurrentPathname(asPath: string | undefined): string {
    const pathnameFromAsPath = asPath ? getPathnameFromRouteUrl(asPath) : '';

    if (pathnameFromAsPath) {
        return pathnameFromAsPath;
    }

    if (typeof window !== 'undefined') {
        return window.location.pathname;
    }

    return '';
}

export default function DebugErrorCapture() {
    const router = useRouter();
    const pathnameRef = useRef(getCurrentPathname(router.asPath));
    const hasSyncedInitialPathnameRef = useRef(false);

    const reportError = useCallback(
        (error: Error, logMessage: string, source: DebugErrorDetail['source']) => {
            appLogger.error(logMessage, undefined, error);
            captureDebugError({
                message: error.message || 'Unknown error',
                stack: error.stack,
                source,
            });
        },
        [],
    );

    useEffect(() => {
        if (hasSyncedInitialPathnameRef.current) {
            return;
        }

        const pathname = getCurrentPathname(router.asPath);

        if (!pathname) {
            return;
        }

        pathnameRef.current = pathname;
        hasSyncedInitialPathnameRef.current = true;
    }, [router.asPath]);

    useEffect(() => {
        const handleRouteChangeComplete = (url: string) => {
            const nextPathname = getPathnameFromRouteUrl(url);

            if (nextPathname === pathnameRef.current) {
                return;
            }

            pathnameRef.current = nextPathname;
            clearDebugError();
        };

        router.events.on('routeChangeComplete', handleRouteChangeComplete);

        return () => {
            router.events.off('routeChangeComplete', handleRouteChangeComplete);
        };
    }, [router.events]);

    useEffect(() => {
        if (!isAppDebug()) {
            return undefined;
        }

        return registerDebugErrorWindowCapture(reportError);
    }, [reportError]);

    return null;
}
